import React, { useState, useEffect } from "react";
import { Progress } from "reactstrap";
import DataTable from "react-data-table-component";
import useCustomMutation from "../../../../Hooks/useCustomMutation";
import ModalDelete from "../../../Modal/ModalDelete";
import OffCanvas from "../../../OffCanvas/OffCanvas";
import { AiFillEye } from "react-icons/ai";
import { FaEdit } from "react-icons/fa";
import { AiFillDelete } from "react-icons/ai";
import DeleteTicketForm from "./DeleteTicketForm";
import ViewTicket from "./ViewTicket";
import EditTicket from "./EditTicket";

const TableTicket = ({ userID, token, eventID, search, eventDetails }) => {
  const [ticketID, setTicketID] = useState(null);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [isToggleView, setIsToggleView] = useState(false);
  const [isOffcanvasEditOpen, setIsOffcanvasEditOpen] = useState(false);
  const [isToggleDelete, setIsToggleDelete] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );

  const key = ["EventDetails", eventID];
  const mutationDelete = useCustomMutation(key);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowWidth < 768;
  const isSmallMobile = windowWidth < 400;

  const toggleViewTicket = (details) => {
    setTicketDetails(details);
    setIsToggleView(!isToggleView);
  };

  const toggleEditTicket = (details) => {
    setTicketDetails(details);
    setIsOffcanvasEditOpen(!isOffcanvasEditOpen);
  };

  const toggleDeleteTicket = (id) => {
    setTicketID(id);
    setIsToggleDelete(!isToggleDelete);
  };

  useEffect(() => {
    if (mutationDelete?.isSuccess) {
      setIsToggleDelete(false);
    }
  }, [mutationDelete?.isSuccess]);

  const filterTickets = (settings, searchQuery) => {
    if (!searchQuery) return settings;
    return settings?.filter((item) =>
      [item?.tier]?.some((field) =>
        field?.toLowerCase()?.includes(searchQuery.toLowerCase())
      )
    );
  };

  const filteredTicketSettings = filterTickets(
    eventDetails?.ticketSettings,
    search
  );
  const data = filteredTicketSettings || [];

  const customStyles = {
    tableWrapper: {
      style: {
        width: "100%",
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
      },
    },
    headRow: {
      style: {
        backgroundColor: "#f8f9fa",
        borderBottomWidth: "2px",
        borderBottomColor: "#e9ecef",
        minHeight: "48px",
      },
    },
    headCells: {
      style: {
        fontSize: isMobile ? "10px" : "12px",
        fontWeight: "700",
        color: "#495057",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        paddingLeft: isMobile ? "8px" : "12px",
        paddingRight: isMobile ? "8px" : "12px",
        whiteSpace: "nowrap",
      },
    },
    cells: {
      style: {
        fontSize: isMobile ? "11px" : "13px",
        color: "#212529",
        paddingLeft: isMobile ? "8px" : "12px",
        paddingRight: isMobile ? "8px" : "12px",
        minHeight: "60px",
      },
    },
    rows: {
      style: {
        "&:hover": {
          backgroundColor: "#f8f9fa",
        },
        borderBottomWidth: "1px",
        borderBottomColor: "#e9ecef",
      },
    },
    pagination: {
      style: {
        backgroundColor: "#ffffff",
        borderTopWidth: "1px",
        borderTopColor: "#e9ecef",
        minHeight: "52px",
        fontSize: "13px",
      },
    },
  };

  // Button sizing based on screen size
  const getButtonSize = () => {
    if (isSmallMobile) return { button: 32, icon: 14, gap: 6 };
    if (isMobile) return { button: 36, icon: 16, gap: 8 };
    return { button: 40, icon: 18, gap: 10 };
  };

  const buttonSizes = getButtonSize();

  const columns = [
    {
      name: <span>Ticket Name</span>,
      selector: (row) => row?.tier || "N/A",
      sortable: true,
      grow: 2,
      style: {
        fontWeight: "600",
        fontSize: isMobile ? "12px" : "14px",
      },
      cell: (row) => (
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            paddingRight: "8px",
          }}
          title={row?.tier}
        >
          {row?.tier || "N/A"}
        </div>
      ),
    },
    {
      name: <span>Price</span>,
      selector: (row) => row?.price,
      sortable: true,
      style: {
        fontWeight: "700",
        fontSize: isMobile ? "12px" : "14px",
        color: "#f68a20",
      },
      cell: (row) => (
        <div style={{ paddingRight: "8px" }}>
          ${row?.price || "0.00"}
        </div>
      ),
    },
    {
      name: <span>Stats</span>,
      cell: (row) => {
        return (
          <div style={{ width: "100%", paddingRight: "8px" }}>
            <Progress
              color="success"
              value={row?.quantitySold || 0}
              style={{
                height: isMobile ? "6px" : "10px",
                marginBottom: "4px",
                borderRadius: "6px",
              }}
            />
            <div
              style={{
                fontSize: isMobile ? "9px" : "11px",
                textAlign: "center",
                color: "#6b7280",
                fontWeight: "500",
              }}
            >
              {row?.quantitySold || 0}%
            </div>
          </div>
        );
      },
      minWidth: isMobile ? "90px" : "120px",
    },
    {
      name: <span>Actions</span>,
      cell: (row) => {
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: `${buttonSizes.gap}px`,
              flexWrap: "nowrap",
              width: "auto",
              minWidth: `${buttonSizes.button * 3 + buttonSizes.gap * 2}px`,
            }}
          >
            <button
              onClick={() => toggleViewTicket(row)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: `${buttonSizes.button}px`,
                height: `${buttonSizes.button}px`,
                background: "linear-gradient(135deg, #0d6efd 0%, #0b5ed7 100%)",
                color: "#ffffff",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow: "0 2px 6px rgba(13, 110, 253, 0.35)",
                flexShrink: 0,
                padding: 0,
              }}
              title="View Ticket"
              type="button"
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(13, 110, 253, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 6px rgba(13, 110, 253, 0.35)";
              }}
            >
              <AiFillEye size={buttonSizes.icon} />
            </button>

            <button
              onClick={() => toggleEditTicket(row)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: `${buttonSizes.button}px`,
                height: `${buttonSizes.button}px`,
                background: "linear-gradient(135deg, #ffc107 0%, #ffb300 100%)",
                color: "#000000",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow: "0 2px 6px rgba(255, 193, 7, 0.35)",
                flexShrink: 0,
                padding: 0,
              }}
              title="Edit Ticket"
              type="button"
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(255, 193, 7, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 6px rgba(255, 193, 7, 0.35)";
              }}
            >
              <FaEdit size={buttonSizes.icon} />
            </button>

            <button
              onClick={() => toggleDeleteTicket(row?._id)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: `${buttonSizes.button}px`,
                height: `${buttonSizes.button}px`,
                background: "linear-gradient(135deg, #dc3545 0%, #c82333 100%)",
                color: "#ffffff",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow: "0 2px 6px rgba(220, 53, 69, 0.35)",
                flexShrink: 0,
                padding: 0,
              }}
              title="Delete Ticket"
              type="button"
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(220, 53, 69, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 6px rgba(220, 53, 69, 0.35)";
              }}
            >
              <AiFillDelete size={buttonSizes.icon} />
            </button>
          </div>
        );
      },
      center: false,
      minWidth: isSmallMobile ? "130px" : isMobile ? "150px" : "180px",
    },
  ];

  return (
    <div style={styles.container}>
      {data.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>🎫</div>
          <h4 style={styles.emptyTitle}>No tickets found</h4>
          <p style={styles.emptyText}>
            {search ? `No results for "${search}"` : "Start by adding your first ticket"}
          </p>
        </div>
      ) : (
        <div style={styles.tableWrapper}>
          <DataTable
            columns={columns}
            data={data}
            responsive
            pagination
            paginationPerPage={10}
            paginationRowsPerPageOptions={[10, 25, 50]}
            customStyles={customStyles}
            highlightOnHover
            noDataComponent={
              <div style={styles.noData}>No records to display</div>
            }
          />
        </div>
      )}

      <ModalDelete
        size="md"
        isOpen={isToggleDelete}
        toggle={() => {
          setIsToggleDelete(false);
        }}
        title="Delete Ticket"
        body={
          <DeleteTicketForm
            setIsToggleDelete={setIsToggleDelete}
            eventID={eventID}
            ticketID={ticketID}
            token={token}
            pending={mutationDelete?.isLoading}
          />
        }
      />

      <OffCanvas
        isOffcanvasOpen={isToggleView}
        title="View Ticket"
        body={<ViewTicket details={ticketDetails} />}
        toggleSaveChanges={() => toggleViewTicket()}
        directionOffCanvas="end"
        widthOffCanvas={isMobile ? "100%" : 700}
      />

      <OffCanvas
        isOffcanvasOpen={isOffcanvasEditOpen}
        title="Edit Ticket"
        body={
          <EditTicket
            ticketDetails={ticketDetails}
            token={token}
            eventDetails={eventDetails}
            setIsOffcanvasOpen={setIsOffcanvasEditOpen}
          />
        }
        toggleSaveChanges={toggleEditTicket}
        directionOffCanvas="end"
        widthOffCanvas={isMobile ? "100%" : 700}
      />
    </div>
  );
};

const styles = {
  container: {
    width: "100%",
    maxWidth: "100%",
    padding: "16px",
    boxSizing: "border-box",
    backgroundColor: "#ffffff",
    borderRadius: "10px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
    border: "1px solid #e9ecef",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 20px",
    minHeight: "250px",
    textAlign: "center",
  },
  emptyIcon: {
    fontSize: "48px",
    marginBottom: "16px",
  },
  emptyTitle: {
    margin: "0 0 8px 0",
    fontSize: "18px",
    fontWeight: "600",
    color: "#212529",
  },
  emptyText: {
    margin: 0,
    fontSize: "14px",
    color: "#6c7280",
  },
  tableWrapper: {
    width: "100%",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    borderRadius: "8px",
  },
  noData: {
    padding: "40px 20px",
    textAlign: "center",
    color: "#6c757d",
    fontSize: "14px",
  },
};

export default TableTicket;